import { Search, Eye, EyeOff, Archive } from 'lucide-react';
import type { OnboardingPersonStatus } from '../../hooks/onboardingOps';

/**
 * The onboarding dashboard's filter toolbar: search, contract and
 * start-date selects, the hidden-people toggle, and the status pills.
 * Pure controlled inputs; all state lives in the dashboard.
 */

export type DashboardStatusFilter = 'all' | OnboardingPersonStatus;
export type DashboardStartWindow = 'any' | 'week' | 'month' | 'none';

const STATUS_LABELS: Record<DashboardStatusFilter, string> = {
  all: 'All',
  no_tasks: 'No tasks',
  not_started: 'Not started',
  in_progress: 'In progress',
  awaiting_approval: 'Complete - Pending approval',
  complete: 'Complete',
};

export function DashboardFilters({
  q,
  onQ,
  contracts,
  contract,
  onContract,
  startWindow,
  onStartWindow,
  hiddenCount,
  showHidden,
  onToggleShowHidden,
  finishedCount,
  showFinished,
  onToggleShowFinished,
  status,
  onStatus,
}: {
  q: string;
  onQ: (v: string) => void;
  contracts: string[];
  contract: string;
  onContract: (v: string) => void;
  startWindow: DashboardStartWindow;
  onStartWindow: (v: DashboardStartWindow) => void;
  hiddenCount: number;
  showHidden: boolean;
  onToggleShowHidden: () => void;
  /** Count of finished (archived) people, shown once the toggle has loaded them. */
  finishedCount: number;
  showFinished: boolean;
  onToggleShowFinished: () => void;
  status: DashboardStatusFilter;
  onStatus: (v: DashboardStatusFilter) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <input
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder="Search by name or email"
            className="h-9 w-full rounded-token-sm border border-line-strong/15 bg-surface pl-8 pr-3 text-sm"
          />
        </div>
        {contracts.length > 0 ? (
          <select
            value={contract}
            onChange={(e) => onContract(e.target.value)}
            className="h-9 rounded-token-sm border border-line-strong/15 bg-surface px-2 text-sm"
          >
            <option value="all">All contracts</option>
            {contracts.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        ) : null}
        <select
          value={startWindow}
          onChange={(e) => onStartWindow(e.target.value as DashboardStartWindow)}
          className="h-9 rounded-token-sm border border-line-strong/15 bg-surface px-2 text-sm"
        >
          <option value="any">Any start date</option>
          <option value="week">Starts in 7 days</option>
          <option value="month">Starts in 30 days</option>
          <option value="none">No start date</option>
        </select>
        {hiddenCount > 0 ? (
          <button
            onClick={onToggleShowHidden}
            className={`inline-flex items-center gap-1.5 rounded-token-sm px-3 py-1.5 text-xs font-medium ${
              showHidden ? 'bg-accent text-fg-on-accent' : 'bg-surface-2 text-fg-muted'
            }`}
          >
            {showHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Hidden ({hiddenCount})
          </button>
        ) : null}
        {/* Always available: finishedCount is only known once the toggle
            has pulled archived people in, so this can't gate on a count. */}
        <button
          onClick={onToggleShowFinished}
          className={`inline-flex items-center gap-1.5 rounded-token-sm px-3 py-1.5 text-xs font-medium ${
            showFinished ? 'bg-accent text-fg-on-accent' : 'bg-surface-2 text-fg-muted'
          }`}
        >
          <Archive className="h-3.5 w-3.5" />
          {showFinished ? `Finished (${finishedCount})` : 'Show finished'}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(STATUS_LABELS) as DashboardStatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => onStatus(f)}
            className={`rounded-token-sm px-3 py-1.5 text-xs font-medium ${
              status === f ? 'bg-accent text-fg-on-accent' : 'bg-surface-2 text-fg-muted'
            }`}
          >
            {STATUS_LABELS[f]}
          </button>
        ))}
      </div>
    </div>
  );
}
